import { Form, Button, Modal } from "react-bootstrap";
import AppLoader from 'components/general/AppLoader';
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import { useState, useEffect, useCallback } from "react"; // Added useCallback
import { EditorState, convertToRaw } from "draft-js";
import { Editor } from "react-draft-wysiwyg";
import draftToHtml from "draftjs-to-html";

export default function AddNewModal({ getAnnouncements }) {
  const { user: authUser } = useSelector((x) => x.auth);

  const url = process.env.REACT_APP_URL;
  const [to, setTo] = useState("Officer");
  const [title, setTitle] = useState("");
  const [shortDescription, setShortDescription] = useState(EditorState.createEmpty());
  const [description, setDescription] = useState(EditorState.createEmpty());
  const [saveLoading, setSaveLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);

  const handleCloseModal = () => setShowModal(false);
  const handleShowModal = () => setShowModal(true);

  const handleSubmit = useCallback(
    async (e) => {
      if (e) e.preventDefault();
      setSaveLoading(true);

      const shortDescriptionHtml = draftToHtml(convertToRaw(shortDescription.getCurrentContent()));
      const descriptionHtml = draftToHtml(convertToRaw(description.getCurrentContent()));

      // Validation for character limits
      if (title.length > 100) {
        toast.error("Title must be 100 characters or less.", { position: "bottom-left" });
        setSaveLoading(false);
        return;
      }

      if (shortDescription.getCurrentContent().getPlainText().length > 200) {
        toast.error("Short description must be 200 characters or less.", { position: "bottom-left" });
        setSaveLoading(false);
        return;
      }

      if (description.getCurrentContent().getPlainText().length > 2500) {
        toast.error("Description must be 2500 characters or less.", { position: "bottom-left" });
        setSaveLoading(false);
        return;
      }

      try {
        const response = await fetch(`${url}/announcement`, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${authUser.token}`,
          },
          body: JSON.stringify({
            to,
            title,
            shortDescription: shortDescriptionHtml,
            description: descriptionHtml,
            active: true,
          }),
        });

        if (response.status === 201) {
          const data = await response.json();
          toast.success(`Announcement ${data.title} added successfully`, {
            position: "bottom-left",
          });
          getAnnouncements(); // Refresh the announcements list
          handleCloseModal();
        } else {
          const errorData = await response.json();
          toast.error(`Failed to add announcement: ${errorData.error || "Unknown error"}`, {
            position: "bottom-left",
          });
        }
      } catch (error) {
        console.error("Error adding announcement:", error);
        toast.error("Failed to add announcement. Please try again later.", { position: "bottom-left" });
      } finally {
        setSaveLoading(false);
      }
    },
    [authUser.token, description, getAnnouncements, shortDescription, title, to, url]
  );

  // Add CTRL+S (or CMD+S) keyboard shortcut
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === "s") {
        e.preventDefault();
        if (showModal && !saveLoading) {
          handleSubmit(); // Call handleSubmit without an event object
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [showModal, saveLoading, handleSubmit]); // Added handleSubmit to the dependency array

  return (
    <>
      <Button onClick={handleShowModal} variant="success" className="mb-1 float-end">
        Add New Announcement
      </Button>

      <Modal show={showModal} onHide={handleCloseModal} data-bs-theme="dark" size="lg" style={{ top: "2%" }}>
        <Modal.Header closeButton>
          <Modal.Title>Add New Announcement</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleSubmit}>
            <Form.Group controlId="formTo" className="mb-3">
              <Form.Label>Audience</Form.Label>
              <Form.Select
                aria-label="To"
                className="formInput"
                name="to"
                value={to}
                onChange={(e) => setTo(e.target.value)}
              >
                <option value="Officer">Officers</option>
                <option value="Member">Members</option>
                <option value="Guest">Guests</option>
                <option value="Public">Public</option>
              </Form.Select>
            </Form.Group>

            <Form.Group controlId="formTitle" className="mb-3">
              <Form.Label>Title</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter announcement title (max 100 characters)"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
              />
            </Form.Group>

            <Form.Group controlId="formShortDescription" className="mb-3">
              <Form.Label>Short Description</Form.Label>
              <Editor
                editorState={shortDescription}
                wrapperClassName="demo-wrapper"
                toolbarClassName="toolbar-class"
                editorClassName="editor-class"
                onEditorStateChange={setShortDescription}
                placeholder="Enter announcement short description (max 200 characters)"
                toolbar={{
                  options: ["inline", "blockType", "list", "textAlign", "link"],
                  inline: { options: ["bold", "italic", "underline"] },
                  blockType: { options: ["Normal", "H4", "H5", "H6"] },
                  list: { options: ["unordered", "ordered"] },
                  textAlign: { inDropdown: false },
                  link: { inDropdown: false },
                }}
              />
            </Form.Group>

            <Form.Group controlId="formDescription" className="mb-3">
              <Form.Label>Full Description</Form.Label>
              <Editor
                editorState={description}
                wrapperClassName="demo-wrapper"
                toolbarClassName="toolbar-class"
                editorClassName="editor-class"
                onEditorStateChange={setDescription}
                placeholder="Enter announcement full description (max 2500 characters)"
                toolbar={{
                  options: ["inline", "blockType", "list", "textAlign", "link"],
                  inline: { options: ["bold", "italic", "underline"] },
                  blockType: { options: ["Normal", "H4", "H5", "H6"] },
                  list: { options: ["unordered", "ordered"] },
                  textAlign: { inDropdown: false },
                  link: { inDropdown: false },
                }}
              />
            </Form.Group>

            {saveLoading ? (
              <AppLoader label="Loading..." />
            ) : (
              <Button variant="primary" type="submit" className="w-100 mt-3">
                Save Changes
              </Button>
            )}
          </Form>
        </Modal.Body>
      </Modal>
    </>
  );
}